import { createContext, useContext } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "./supabase";
import type { Snapshot } from "@/engine/types";
import { migrarSnapshot } from "@/engine/migrate";
import type { SnapshotRow } from "@/types/db";

export interface FotoActual { row: SnapshotRow; snapshot: Snapshot }
interface FuenteSnapshot { foto: FotoActual | null; progreso: Partial<SnapshotRow>[] }
/** En modo ejemplo la foto no viene de Supabase sino de los datos del ejemplo (calculados en el browser). */
export const FuenteSnapshotCtx = createContext<FuenteSnapshot | null>(null);

type Res<T> = { data: T | undefined; isLoading: boolean; isError: boolean; refetch: () => unknown };

/** Snapshot vigente del miembro (RLS: solo el suyo). */
export function useSnapshotActual(): Res<FotoActual | null> {
  const fuente = useContext(FuenteSnapshotCtx);
  const q = useQuery({
    queryKey: ["snapshot", "current"],
    enabled: !fuente,
    queryFn: async (): Promise<FotoActual | null> => {
      const { data, error } = await supabase.from("snapshots").select("*").eq("is_current", true).maybeSingle();
      if (error) throw error;
      if (!data) return null;
      const row = data as SnapshotRow;
      return { row, snapshot: migrarSnapshot(row.output as Snapshot) };
    },
  });
  if (fuente) return { data: fuente.foto, isLoading: false, isError: false, refetch: () => undefined };
  return q;
}

export function useProgreso(): Res<Partial<SnapshotRow>[]> {
  const fuente = useContext(FuenteSnapshotCtx);
  const q = useQuery({
    queryKey: ["snapshot", "progress"],
    enabled: !fuente,
    queryFn: async () => {
      const { data, error } = await supabase.from("v_my_progress").select("*");
      if (error) throw error;
      return data as Partial<SnapshotRow>[];
    },
  });
  if (fuente) return { data: fuente.progreso, isLoading: false, isError: false, refetch: () => undefined };
  return q;
}
